import { useEffect, useRef, useState } from "react";

function buildWsUrl(baseUrl, token) {
  // keep full ws/wss/http/https URLs as-is
  const hasScheme = /^(ws|wss|http|https):\/\//i.test(baseUrl);
  let url = baseUrl;

  // add scheme if missing
  if (!hasScheme) {
    const isHttps =
      typeof window !== "undefined" && window.location?.protocol === "https:";
    const scheme = isHttps ? "wss://" : "ws://";
    url = scheme + baseUrl.replace(/^\/+/, "");
  }

  // append token as query (?token=...), if provided
  if (token) {
    const join = url.includes("?") ? "&" : "?";
    url = `${url}${join}token=${encodeURIComponent(token)}`;
  }
  return url;
}

export default function useIndividualStream({
  url = process.env.REACT_APP_INDIVIDUAL_SOCKET ||
    "192.168.50.163:8080/individual",
  token = (typeof localStorage !== "undefined" &&
    localStorage.getItem("token")) ||
    "",
  enabled = true,
} = {}) {
  const [status, setStatus] = useState(enabled ? "connecting" : "idle");
  const [error, setError] = useState(null);
  const [lastItem, setLastItem] = useState(null);

  const wsRef = useRef(null);

  // --- reconnect machinery (1s cadence) ---
  const reconnectTimerRef = useRef(null);
  const shouldReconnectRef = useRef(false);
  const manualCloseRef = useRef(false);

  // --- render-throttle machinery ---
  const pendingItemRef = useRef(null);
  const rafIdRef = useRef(null);
  const scheduleFlush = () => {
    if (rafIdRef.current != null) return; // already scheduled
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      if (pendingItemRef.current !== null) {
        setLastItem(pendingItemRef.current);
        pendingItemRef.current = null;
      }
    });
  };

  const clearTimers = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (rafIdRef.current != null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  };

  const tryParseAndQueue = (txt) => {
    try {
      const obj = JSON.parse(txt);
      pendingItemRef.current = obj; // coalesce many messages -> one render
      scheduleFlush();
      return true;
    } catch {
      return false;
    }
  };

  const openSocket = () => {
    const fullUrl = buildWsUrl(url, token);

    try {
      setStatus("connecting");
      setError(null);

      const ws = new WebSocket(fullUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        clearTimers();
        setStatus("connected");
      };

      ws.onmessage = async (evt) => {
        const data = evt.data;

        if (typeof data === "string") {
          if (!tryParseAndQueue(data)) {
            data
              .split(/\r?\n/)
              .map((s) => s.trim())
              .filter(Boolean)
              .forEach((line) => tryParseAndQueue(line));
          }
          return;
        }

        if (data instanceof Blob) {
          const text = await data.text();
          if (!tryParseAndQueue(text)) {
            text
              .split(/\r?\n/)
              .map((s) => s.trim())
              .filter(Boolean)
              .forEach((line) => tryParseAndQueue(line));
          }
          return;
        }

        if (data instanceof ArrayBuffer) {
          const text = new TextDecoder().decode(new Uint8Array(data));
          if (!tryParseAndQueue(text)) {
            text
              .split(/\r?\n/)
              .map((s) => s.trim())
              .filter(Boolean)
              .forEach((line) => tryParseAndQueue(line));
          }
        }
      };

      ws.onerror = (e) => {
        console.error("WS error:", e);
        setError(new Error("WebSocket error"));
        setStatus("error");
      };

      ws.onclose = () => {
        wsRef.current = null;
        setStatus("disconnected");

        // auto-reconnect (once per second) unless manually disconnected
        if (shouldReconnectRef.current && !manualCloseRef.current && enabled) {
          if (!reconnectTimerRef.current) {
            reconnectTimerRef.current = setTimeout(() => {
              reconnectTimerRef.current = null;
              if (!wsRef.current && shouldReconnectRef.current && enabled) {
                openSocket();
              }
            }, 1000);
          }
        }
      };
    } catch (e) {
      console.error("WS setup error:", e);
      setError(e instanceof Error ? e : new Error(String(e)));
      setStatus("error");

      if (shouldReconnectRef.current && !manualCloseRef.current && enabled) {
        if (!reconnectTimerRef.current) {
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            openSocket();
          }, 1000);
        }
      }
    }
  };

  useEffect(() => {
    if (!enabled) {
      setStatus("idle");
      return;
    }
    if (!url) {
      setStatus("error");
      setError(new Error("No URL provided"));
      return;
    }

    manualCloseRef.current = false;
    shouldReconnectRef.current = true;
    openSocket();

    return () => {
      shouldReconnectRef.current = false; // stop future reconnects
      manualCloseRef.current = true; // treat as manual
      clearTimers();
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, token, enabled]);

  // public helpers: same names, same semantics
  const reconnect = () => {
    manualCloseRef.current = false;
    shouldReconnectRef.current = true;
    clearTimers();
    try {
      wsRef.current?.close(); // triggers onclose -> reconnect in 1s
    } catch {}
    if (!wsRef.current) openSocket(); // if already closed, open now
  };

  const disconnect = () => {
    shouldReconnectRef.current = false;
    manualCloseRef.current = true;
    clearTimers();
    try {
      wsRef.current?.close();
    } catch {}
  };

  return { status, error, lastItem, wsRef, reconnect, disconnect };
}
